import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";

export const generateReviewSchema = (quotes, companyName = "Mangomint") => {
  if (!quotes || quotes.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: companyName,
    review: quotes.map((quote) => ({
      "@type": "Review",
      reviewBody: quote.quote,
      author: {
        "@type": "Person",
        name: quote.author.replace(/<[^>]*>/g, ""), // Strip HTML
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
      },
    })),
  };
};

export const generateComparisonSchema = (mangomintName, competitorName, tableRows) => {
  if (!mangomintName || !competitorName) return null;

  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: mangomintName,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "500+",
      bestRating: "5",
    },
    brand: {
      "@type": "Brand",
      name: "Mangomint",
    },
    category: "Salon & Spa Management Software",
    description: `Compare ${mangomintName} vs ${competitorName} features, pricing, and user reviews to find the best salon and spa management software for your business.`,
  };

  if (tableRows && tableRows.length > 0) {
    const features = tableRows
      .filter((row) => row.category && typeof row.mangomint !== "undefined")
      .map((row) => {
        const feature = {
          "@type": "PropertyValue",
          name: row.category,
        };

        if (typeof row.mangomint === "boolean") {
          feature.value = row.mangomint ? "Yes" : "No";
        } else if (typeof row.mangomint === "object" && row.mangomint !== null) {
          if (row.mangomint.capterra || row.mangomint.g2) {
            feature.value = `Capterra: ${row.mangomint.capterra || "N/A"}, G2: ${row.mangomint.g2 || "N/A"}`;
          } else if (row.mangomint.desktop || row.mangomint.mobile) {
            feature.value = row.mangomint.desktop || row.mangomint.mobile;
          }
        } else {
          feature.value = String(row.mangomint);
        }

        return feature;
      });

    if (features.length > 0) {
      baseSchema.additionalProperty = features;
    }
  }

  return baseSchema;
};

export const generateSoftwareAppSchema = (companyName = "Mangomint") => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: companyName,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "500",
    bestRating: "5",
  },
  creator: {
    "@type": "Organization",
    name: "Mangomint",
    url: "https://mangomint.com",
  },
});

export const generateCompetitorSchemas = (competitorData) => {
  if (!competitorData) return {};

  const schemas = {};

  if (competitorData.quotes?.length > 0) {
    schemas.reviews = generateReviewSchema(competitorData.quotes);
  }

  schemas.comparison = generateComparisonSchema(
    competitorData.table?.mangomintName || "Mangomint",
    competitorData.competitor?.name,
    competitorData.table?.rows
  );

  schemas.software = generateSoftwareAppSchema();

  return schemas;
};

export const generateFaqSchema = (faqs, name = "FAQs") => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  name,
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.plainTextAnswer,
    },
  })),
});

export const generateChangelogSchema = (updates, softwareName = "Mangomint") => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: `${softwareName} Product Updates`,
  description: `Latest product updates and new features for ${softwareName}`,
  itemListElement: updates.map((update, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Article",
      headline: update.title,
      description: update.description?.description || update.description,
      datePublished: update.overridePublishDate || update.createdAt,
      dateModified: update.updatedAt,
      author: {
        "@type": "Organization",
        name: "Mangomint",
      },
      publisher: {
        "@type": "Organization",
        name: "Mangomint",
        logo: {
          "@type": "ImageObject",
          url: "/images/mangomint.jpg",
        },
      },
      about: {
        "@type": "SoftwareApplication",
        name: softwareName,
      },
      keywords: update.category,
    },
  })),
});

export const generateLandingPageSchema = (pageData) => {
  const { seoMetadata, actionSections, slug } = pageData;
  const schemas = {};

  let competitorData = null;
  if (slug && typeof require !== "undefined") {
    try {
      const competitorDataModule = require("@components/shared/landingPage/competitor/competitorData");
      competitorData = competitorDataModule.default?.[slug];
    } catch (e) {
      // Ignore if can't import
    }
  }

  const faqSection = actionSections?.find(
    (section) =>
      section.__typename === "ContentfulGenericList" && section.listItems?.[0]?.__typename === "ContentfulListItemFaq"
  );

  const faqs =
    faqSection?.listItems?.map((item) => ({
      question: item.question,
      plainTextAnswer: item.richTextAnswer?.raw ? documentToPlainTextString(JSON.parse(item.richTextAnswer.raw)) : "",
    })) || [];

  if (faqs.length > 0) {
    schemas.faq = generateFaqSchema(faqs, seoMetadata?.title || "FAQs");
  }

  if (competitorData) {
    const competitorSchemas = generateCompetitorSchemas(competitorData);
    Object.assign(schemas, competitorSchemas);
  }

  return schemas;
};
